import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"

import type { QuizAttemptSummary } from "@/client"
import { AttemptsService } from "@/client"
import { AttemptsList } from "@/components/Attempts/AttemptsList"

interface QuizLandingProps {
  quizId: string
  quiz: {
    name: string
    description?: string | null
    time_limit_minutes: number | null | undefined
    allow_retake: boolean
    cutoff_date?: string | null
    question_count: number
  }
  onStart: () => void
  isStarting: boolean
  hasActiveAttempt: boolean
  timeRemainingSeconds?: number | null
  startedAt?: string | null
  isStartDisabled?: boolean
  disabledReason?: {
    title: string
    description: string
  } | null
  actionError?: string | null
}

export function QuizLanding({
  quizId,
  quiz,
  onStart,
  isStarting,
  hasActiveAttempt,
  timeRemainingSeconds,
  startedAt,
  isStartDisabled = false,
  disabledReason = null,
  actionError,
}: QuizLandingProps) {
  const { data: attemptsData, isLoading: isLoadingAttempts } = useQuery({
    queryKey: ["my-attempts", quizId],
    queryFn: () =>
      AttemptsService.getMyQuizAttempts({ skip: 0, limit: 100, quizId }),
  })

  const attempts = (attemptsData?.data ?? []) as QuizAttemptSummary[]

  const formattedCutoff = quiz.cutoff_date
    ? new Date(quiz.cutoff_date).toLocaleString()
    : null

  const timeRemainingMinutes =
    typeof timeRemainingSeconds === "number"
      ? Math.max(Math.floor(timeRemainingSeconds / 60), 0)
      : null

  const startedAtDisplay = startedAt
    ? new Date(startedAt).toLocaleString()
    : null

  return (
    <VStack gap={8} align="stretch">
      <Card.Root>
        <CardHeader>
          <Heading size="lg">{quiz.name}</Heading>
        </CardHeader>
        <CardBody>
          <Stack gap={6}>
            {quiz.description ? (
              <Text color="fg.muted">{quiz.description}</Text>
            ) : null}

            <Stack direction={{ base: "column", md: "row" }} gap={4}>
              <InfoTile label="Questions" value={`${quiz.question_count}`} />
              <InfoTile
                label="Time Limit"
                value={
                  quiz.time_limit_minutes
                    ? `${quiz.time_limit_minutes} minutes`
                    : "None"
                }
              />
              <InfoTile
                label="Retakes"
                value={quiz.allow_retake ? "Allowed" : "Not allowed"}
                badgeColor={quiz.allow_retake ? "green" : "gray"}
              />
              {formattedCutoff ? (
                <InfoTile label="Due" value={formattedCutoff} />
              ) : null}
            </Stack>

            {hasActiveAttempt ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                borderColor="border.muted"
                p={4}
                bg="surface.subtle"
              >
                <Stack gap={2}>
                  <Heading size="sm">You have an active attempt</Heading>
                  {startedAtDisplay ? (
                    <Text color="fg.muted">
                      Started at: <strong>{startedAtDisplay}</strong>
                    </Text>
                  ) : null}
                  {typeof timeRemainingMinutes === "number" ? (
                    <Text color="fg.muted">
                      Time remaining:{" "}
                      <strong>{timeRemainingMinutes} minutes</strong>
                    </Text>
                  ) : null}
                </Stack>
              </Box>
            ) : null}

            <Button
              colorPalette="blue"
              size="lg"
              onClick={() => {
                if (isStartDisabled) return
                onStart()
              }}
              loading={isStarting}
              alignSelf="flex-start"
              disabled={isStartDisabled}
            >
              {hasActiveAttempt ? "Resume Quiz" : "Start Quiz"}
            </Button>

            {disabledReason ? (
              <Alert.Root status="warning" data-testid="quiz-locked-alert">
                <Alert.Title>{disabledReason.title}</Alert.Title>
                <Alert.Description>
                  {disabledReason.description}
                </Alert.Description>
              </Alert.Root>
            ) : null}

            {!disabledReason && actionError ? (
              <Alert.Root status="error">
                <Alert.Title>Unable to start quiz</Alert.Title>
                <Alert.Description>{actionError}</Alert.Description>
              </Alert.Root>
            ) : null}
          </Stack>
        </CardBody>
      </Card.Root>

      <Card.Root>
        <CardHeader>
          <Heading size="md">Your Attempts</Heading>
        </CardHeader>
        <CardBody>
          {isLoadingAttempts ? (
            <Text color="fg.muted">Loading attempts…</Text>
          ) : attempts.length ? (
            <AttemptsList attempts={attempts} />
          ) : (
            <Text color="fg.muted">No attempts yet.</Text>
          )}
        </CardBody>
      </Card.Root>
    </VStack>
  )
}

function InfoTile({
  label,
  value,
  badgeColor,
}: {
  label: string
  value: string
  badgeColor?: string
}) {
  return (
    <Card.Root variant="subtle" borderWidth="1px" flex="1">
      <CardBody>
        <Stack gap={2}>
          <Text fontSize="sm" color="fg.muted">
            {label}
          </Text>
          <HStack>
            {badgeColor ? (
              <Badge colorPalette={badgeColor}>{value}</Badge>
            ) : (
              <Heading size="sm">{value}</Heading>
            )}
          </HStack>
        </Stack>
      </CardBody>
    </Card.Root>
  )
}
